import type { CacheDateAndKey } from './types';
import type { IUser } from '@/types/models';
import { isObjectEmpty } from '@/utils/appUtil';
import {
  AppAuthRefeshTokenKey,
  AppAuthTokenCreatedKey,
  AppAuthTokenExpireKey,
  AppAuthTokenKey,
  CacheDateCheckKey,
  CacheKey,
  CacheTurnTrackerAlwaysShowAllCheckedInKey,
  FcmSettingKey,
  FcmTokenKey,
  LatestSyncActiveStatusKey,
  SearchHistoryKey,
  UserCacheKey,
} from '@/consts';
import { getMonthNow, getYearNow } from '@/utils/dateUtil';
import { getTodayInTimezone } from '@/utils/timezoneUtil';
import { useStorage } from '@vueuse/core';

// Add user cache key constant
export const useCache = () => {
  const SearchHistoryList = useStorage(SearchHistoryKey, [] as any[]);
  const cacheKeyCheckList = useStorage(
    CacheDateCheckKey,
    [] as CacheDateAndKey[],
  );
  const cacheYearAvailables = useStorage(
    CacheKey.YEAR_AVAILABLE,
    [] as number[],
  );
  const latestSyncActiveStatus = useStorage(LatestSyncActiveStatusKey, 0);
  const fcmSetting = useStorage(FcmSettingKey, true as boolean);
  const fcmToken = useStorage(FcmTokenKey, null as string | null | undefined);
  const refeshTokenKey = useStorage<string | null>(AppAuthRefeshTokenKey, null);
  const authTokenKey = useStorage<string | null>(AppAuthTokenKey, null);
  const authTokenExpireKey = useStorage<string | null>(
    AppAuthTokenExpireKey,
    null,
  );
  const authTokenCreatedKey = useStorage<string | null>(
    AppAuthTokenCreatedKey,
    null,
  );

  // Add user cache storage
  const cachedUserInfo = useStorage<IUser | null | string>(UserCacheKey, null);

  // Turn tracker
  const cacheTurnTrackerAlwaysShowAllCheckedIn = useStorage(CacheTurnTrackerAlwaysShowAllCheckedInKey, false as boolean);

  const findByKey = (k: string): CacheDateAndKey | undefined => {
    if (cacheKeyCheckList.value && cacheKeyCheckList.value.length > 0) {
      return cacheKeyCheckList.value.find((e: CacheDateAndKey) => e.key == k);
    }
    return undefined;
  };
  const findIndexByKey = (k: string): number => {
    if (!cacheKeyCheckList.value) {
      return -1;
    }
    return cacheKeyCheckList.value.findIndex(
      (e: CacheDateAndKey) => e.key == k,
    );
  };

  const onRemoveKey = (k: string): void => {
    const index = findIndexByKey(k);
    if (index >= 0) {
      cacheKeyCheckList.value.splice(index, 1);
    }
  };
  const onAddNewKey = (k: string): void => {
    const item = findByKey(k);
    if (!cacheKeyCheckList.value) {
      cacheKeyCheckList.value = [];
    }
    if (!item) {
      cacheKeyCheckList.value.push({
        key: k,
        date: getTodayInTimezone(),
      });
    }
  };
  const onUpdateKey = (
    k: string,
    period: 'DAY' | 'MONTH' | 'YEAR' = 'DAY',
  ): void => {
    onRemoveKey(k);
    if (period == 'DAY') {
      onAddNewKey(k);
    } else if (period == 'MONTH') {
      onAddNewMonthKey(k);
    } else if (period == 'YEAR') {
      onAddNewYearKey(k);
    }
  };
  const canFecthToServerToday = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }

    return item.date != getTodayInTimezone();
  };
  const canFecthToServerMonth = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }
    const m = getMonthNow();
    return item.date != m;
  };
  const onAddNewMonthKey = (k: string): void => {
    const item = findByKey(k);
    if (!item) {
      cacheKeyCheckList.value.push({
        key: k,
        date: getMonthNow(),
      });
    }
  };
  const canFecthToServerYear = (k: string): boolean => {
    const item = findByKey(k);
    if (!item) {
      return true;
    }
    const y = getYearNow();
    return item.date != y;
  };
  const onAddNewYearKey = (k: string): void => {
    const item = findByKey(k);
    if (!item) {
      cacheKeyCheckList.value.push({
        key: k,
        date: getYearNow(),
      });
    }
  };
  const destroyCache = () => {
    cacheKeyCheckList.value = null;
    cacheYearAvailables.value = null;
  };

  const logoutClear = () => {
    cachedUserInfo.value = null;
    destroyCache();
  };

  const fetchFromCacheToday = (key: string, cache: any) => {
    const canFetchToServer = canFecthToServerToday(key);
    return !canFetchToServer && cache && !isObjectEmpty(cache);
  };
  const fetchFromCacheMonth = (key: string, cache: any) => {
    const canFetchToServer = canFecthToServerMonth(key);
    return !canFetchToServer && cache && !isObjectEmpty(cache);
  };
  const fetchFromCacheList = (canFetchToServer: boolean, cache: any) => {
    return !canFetchToServer && cache && cache.length > 0;
  };

  // Add user cache management functions
  const saveUserToCache = (user: IUser) => {
    cachedUserInfo.value = JSON.stringify(user);
  };

  const getUserFromCache = (): IUser | null => {
    return cachedUserInfo.value ? JSON.parse(cachedUserInfo.value as string) as IUser : null;
  };

  const clearUserCache = () => {
    cachedUserInfo.value = null;
  };

  // Turn tracker
  const getCacheTurnTrackerAlwaysShowAllCheckedIn = (): boolean => {
    return cacheTurnTrackerAlwaysShowAllCheckedIn.value;
  };
  const setCacheTurnTrackerAlwaysShowAllCheckedIn = (value: boolean) => {
    cacheTurnTrackerAlwaysShowAllCheckedIn.value = value;
  };

  return {
    findByKey,
    onAddNewKey,
    onUpdateKey,
    destroyCache,
    canFecthToServerToday,
    canFecthToServerYear,
    cacheYearAvailables,
    fcmSetting,
    fcmToken,
    refeshTokenKey,
    authTokenKey,
    authTokenExpireKey,
    authTokenCreatedKey,
    logoutClear,
    SearchHistoryList,
    fetchFromCacheToday,
    fetchFromCacheList,
    canFecthToServerMonth,
    fetchFromCacheMonth,
    latestSyncActiveStatus,
    // Add new user cache functions
    cachedUserInfo,
    saveUserToCache,
    getUserFromCache,
    clearUserCache,

    // Turn tracker
    getCacheTurnTrackerAlwaysShowAllCheckedIn,
    setCacheTurnTrackerAlwaysShowAllCheckedIn,
  };
};
